import React, { useState, useEffect, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  ArrowPathIcon,
  FunnelIcon,
  EllipsisVerticalIcon,
  TrashIcon,
  PencilIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  EnvelopeIcon,
  EyeIcon,
  UserPlusIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import {
  UserGroupIcon as UserGroupIconSolid,
} from '@heroicons/react/24/solid';
import axiosInstance from '../api/axiosInstance';
import BreadcrumbNavigation from './BreadcrumbNavigation';
import { useAdminData } from '../../context/AdminDataContext';

// Design System Components
import Button from '../ui/Button';
import { SearchInput } from '../ui/Input';
import Table, { StatusBadge, ActionMenu, TablePagination } from '../ui/Table';
import { ConfirmModal, FormModal } from '../ui/Modal';
import Card, { CardHeader } from '../ui/Card';

// Team-specific components
import TeamModal from './TeamModal';
import EditTeamSidePanel from './EditTeamSidePanel';
import MembersModal from './MembersModal';
import TeamDetailsModal from './TeamDetailsModal';
import EmailSettingsModal from './EmailSettingsModal';
import logger from '../../utils/logger';

const Teams = ({ showNotification }) => {
  const { getTeams } = useAdminData();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showNewTeamModal, setShowNewTeamModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEmailSettingsModal, setShowEmailSettingsModal] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    visibility: ''
  });
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [teamType, setTeamType] = useState('team');
  const teamsPerPage = 30;

  const fetchTeams = useCallback(async (page = 1, append = false) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      let response;
      
      // Use cache for initial load without search/filters
      if (page === 1 && !searchQuery && !filters.type && !filters.status && !filters.visibility) {
        try {

          response = await getTeams();
          if (!response?.teams) {
            throw new Error('No cached data');
          }
          // Format cached response to match expected structure
          response = { data: { teams: response.teams, pagination: response.pagination } };
        } catch (error) {

          response = await axiosInstance.get('/api/business/teams', {
            params: {
              search: searchQuery,
              ...filters,
              page: page,
              limit: teamsPerPage
            }
          });
        }
      } else {
        // Direct API call for searches/filters/pagination
        response = await axiosInstance.get('/api/business/teams', {
          params: {
            search: searchQuery,
            ...filters,
            page: page,
            limit: teamsPerPage
          }
        });
      }

      // Handle successful response with teams data
      if (response.data && response.data.teams) {
        const formattedTeams = response.data.teams.map(team => ({
          ...team,
          id: team.id || team._id,
          _id: team._id || team.id
        }));
        
        if (append && page > 1) {
          setTeams(prevTeams => [...prevTeams, ...formattedTeams]);
        } else {
          setTeams(formattedTeams);
        }

        // Check if there are more pages
        const totalTeams = response.data.pagination?.total || formattedTeams.length;
        const totalPages = response.data.pagination?.pages || Math.ceil(totalTeams / teamsPerPage);
        const hasMorePages = page < totalPages;
        
        setHasMore(hasMorePages);
        setCurrentPage(page);
        setError('');
      } else {
        setTeams([]);
        setError('');
        setHasMore(false);
      }
    } catch (error) {
      logger.error('Error fetching teams:', error);
      if (page === 1) {
        const errorMessage = error.response?.data?.message || 'Failed to load teams';
        setError(errorMessage);
        showNotification && showNotification(errorMessage, 'error');
        setTeams([]);
      }
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [searchQuery, filters, showNotification, getTeams]);

  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    fetchTeams(1, false);
  }, [searchQuery, filters]);

  // Search handler function
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    setSelectedTeams([]);
  }, []);

  // Handle creating team
  const handleCreateTeam = async (teamData) => {
    try {
      // Optimistic update - add new team immediately to UI
      const tempId = `temp-${Date.now()}`;
      const optimisticTeam = {
        id: tempId,
        _id: tempId,
        name: teamData.name,
        description: teamData.description,
        email: teamData.email,
        type: teamType,
        privacy: teamData.privacy || 'private',
        status: 'active',
        memberCount: 1,
        createdAt: new Date().toISOString(),
        visibility: teamData.privacy || 'private'
      };

      setTeams(prev => [optimisticTeam, ...prev]);
      setShowNewTeamModal(false);

      const response = await axiosInstance.post('/api/business/teams', {
        ...teamData,
        type: teamType
      });

      if (response.data && response.data.success) {
        // Replace optimistic entry with real data
        setTeams(prev => prev.map(t =>
          t.id === tempId ? {
            ...response.data.team,
            id: response.data.team.id || response.data.team._id,
            _id: response.data.team._id || response.data.team.id
          } : t
        ));
        showNotification && showNotification(`${teamType === 'team' ? 'Team' : 'Distribution list'} created successfully`, 'success');
        return true;
      }
    } catch (error) {
      logger.error('Error creating team:', error);
      // Remove optimistic entry on error
      setTeams(prev => prev.filter(t => !t.id.toString().startsWith('temp-')));
      const errorMessage = error.response?.data?.message || 'Failed to create team';
      showNotification && showNotification(errorMessage, 'error');
      return false;
    }
  };

  // Handle editing team
  const handleEditTeam = async (teamData) => {
    try {
      const response = await axiosInstance.put(`/api/business/teams/${selectedTeam.id}`, teamData);
      
      if (response.data.success) {
        showNotification && showNotification('Team updated successfully', 'success');
        fetchTeams(1, false);
        setShowEditModal(false);
        setSelectedTeam(null);
        return true;
      }
    } catch (error) {
      logger.error('Error editing team:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update team';
      showNotification && showNotification(errorMessage, 'error');
      return false;
    }
  };

  // Handle deleting team
  const handleDeleteTeam = async (teamId) => {
    // Store the team for potential rollback before any state changes
    const teamToDelete = teams.find(t => t.id === teamId);

    try {
      // Optimistic update - remove team immediately from UI
      setTeams(prev => prev.filter(t => t.id !== teamId));

      const response = await axiosInstance.delete(`/api/business/teams/${teamId}`);

      if (response.data.success) {
        showNotification?.('Team deleted successfully', 'success');
      }
    } catch (error) {
      logger.error('Error deleting team:', error);
      // Rollback optimistic update on error using the stored team
      if (teamToDelete) {
        setTeams(prev => [...prev, teamToDelete]);
      }
      const errorMessage = error.response?.data?.message || 'Failed to delete team';
      showNotification?.(errorMessage, 'error');
    }
  };

  // Handle bulk actions
  const handleBulkDelete = async () => {
    if (selectedTeams.length === 0) return;

    if (window.confirm(`Are you sure you want to delete ${selectedTeams.length} team(s)?`)) {
      await Promise.all(selectedTeams.map(teamId => handleDeleteTeam(teamId)));
      setSelectedTeams([]);
    }
  };

  // Handle row click
  const handleRowClick = (team) => {
    setSelectedTeam(team);
    setShowDetailsModal(true);
  };

  // Handle team action
  const handleTeamAction = (team, action) => {

    setSelectedTeam(team);
    switch (action) {
      case 'edit':

        setShowEditModal(true);
        break;
      case 'members':
        setShowMembersModal(true);
        break;
      case 'email':
        setShowEmailSettingsModal(true);
        break;
      case 'details':
        setShowDetailsModal(true);
        break;
      default:
        break;
    }
  };

  // Table sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedTeams.length === teams.length) {
      setSelectedTeams([]);
    } else {
      setSelectedTeams(teams.map(team => team.id));
    }
  };

  const handleTeamSelect = (teamId, checked) => {
    if (checked) {
      setSelectedTeams(prev => [...prev, teamId]);
    } else {
      setSelectedTeams(prev => prev.filter(id => id !== teamId));
    }
  };

  // Get team type color
  const getTypeColor = (type) => {
    switch (type) {
      case 'team': return 'primary';
      case 'distribution_list': return 'secondary';
      case 'security_group': return 'warning';
      default: return 'gray';
    }
  };

  // Get team status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'gray';
      case 'archived': return 'warning';
      default: return 'gray';
    }
  };

  // Table columns
  const columns = [
    {
      id: 'select',
      header: (
        <input
          type="checkbox"
          className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
          onChange={toggleSelectAll}
          checked={teams.length > 0 && selectedTeams.length === teams.length}
        />
      ),
      cell: (team) => (
        <input
          type="checkbox"
          className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
          checked={selectedTeams.includes(team.id)}
          onChange={(e) => {
            e.stopPropagation();
            handleTeamSelect(team.id, e.target.checked);
          }}
          onClick={(e) => e.stopPropagation()}
        />
      ),
      width: '40px'
    },
    {
      id: 'team',
      header: 'Team',
      cell: (team) => (
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
            {team.type === 'distribution_list' ? (
              <EnvelopeIcon className="w-4 h-4 text-orange-600" />
            ) : (
              <UserGroupIcon className="w-4 h-4 text-orange-600" />
            )}
          </div>
          <div>
            <div className="text-sm font-medium text-gray-900 dark:text-white">{team.name}</div>
            <div className="text-xs text-gray-500">
              {team.email && (
                <span className="mr-2">{team.email}</span>
              )}
              Created {new Date(team.createdAt).toLocaleDateString()}
            </div>
          </div>
        </div>
      ),
      sortable: true
    },
    {
      id: 'type',
      header: 'Type',
      cell: (team) => (
        <StatusBadge 
          status={team.type} 
          color={getTypeColor(team.type)}
        >
          {team.type === 'distribution_list' ? 'Distribution List' : 
           team.type === 'security_group' ? 'Security Group' : 'Team'}
        </StatusBadge>
      ),
      sortable: true
    },
    {
      id: 'members',
      header: 'Members',
      cell: (team) => (
        <div className="flex items-center space-x-1">
          <UserGroupIcon className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-900 dark:text-white">{team.memberCount || 0}</span>
        </div>
      ),
      sortable: true
    },
    {
      id: 'status',
      header: 'Status',
      cell: (team) => (
        <StatusBadge 
          status={team.status} 
          color={getStatusColor(team.status)}
        >
          {team.status}
        </StatusBadge>
      ),
      sortable: true
    },
    {
      id: 'visibility',
      header: 'Visibility',
      cell: (team) => (
        <span className="text-sm text-gray-900 dark:text-white capitalize">
          {team.visibility || 'private'}
        </span>
      ),
      sortable: true
    },
    {
      id: 'actions',
      header: '',
      cell: (team) => (
        <ActionMenu
          items={[
            {
              label: 'View Details',
              icon: <EyeIcon />,
              onClick: () => handleTeamAction(team, 'details')
            },
            {
              label: 'View Team Inbox',
              icon: <EnvelopeIcon />,
              onClick: () => window.location.href = `/teams/${team.id}/inbox`,
              disabled: team.type !== 'distribution_list'
            },
            {
              label: 'Edit Team',
              icon: <PencilIcon />,
              onClick: () => handleTeamAction(team, 'edit')
            },
            {
              label: 'Manage Members',
              icon: <UserPlusIcon />,
              onClick: () => handleTeamAction(team, 'members')
            },
            {
              label: 'Email Settings',
              icon: <CogIcon />,
              onClick: () => handleTeamAction(team, 'email'),
              disabled: team.type !== 'distribution_list'
            },
            { type: 'divider' },
            {
              label: 'Delete',
              icon: <TrashIcon />,
              onClick: () => handleDeleteTeam(team.id),
              className: 'text-red-600 hover:text-red-700',
              confirm: true,
              confirmMessage: `Are you sure you want to delete ${team.name}?`
            }
          ]}
        />
      ),
      width: '60px'
    }
  ];

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-[#111111] overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 bg-white dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-gray-800 shadow-sm">
        <div className="px-6 py-4">
          {/* Title Section */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <UserGroupIconSolid className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Team Management</h1>
                <p className="text-sm text-gray-600">
                  {teams.length} {teams.length === 1 ? 'team' : 'teams'}
                </p>
              </div>
            </div>
          </div>
          
          {/* Breadcrumbs & Actions Row */}
          <div className="flex items-center justify-between mb-3">
            <BreadcrumbNavigation />
            <div className="flex items-center gap-2">
              <div className="flex items-center border border-gray-300 rounded-md overflow-hidden">
                <button
                  onClick={() => setTeamType('team')}
                  className={`px-3 py-1 text-xs font-medium transition-colors ${
                    teamType === 'team'
                      ? 'bg-orange-500 text-white'
                      : 'bg-white dark:bg-[#222222] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#2a2a2a]'
                  }`}
                >
                  Team
                </button>
                <button
                  onClick={() => setTeamType('distribution_list')}
                  className={`px-3 py-1 text-xs font-medium transition-colors ${
                    teamType === 'distribution_list'
                      ? 'bg-orange-500 text-white'
                      : 'bg-white dark:bg-[#222222] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#2a2a2a]'
                  }`}
                >
                  Distribution List
                </button>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowNewTeamModal(true)}
                icon={<PlusIcon />}
              >
                Add {teamType === 'team' ? 'Team' : 'List'}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => fetchTeams(1, false)}
                loading={loading}
                icon={<ArrowPathIcon />}
              >
                Refresh
              </Button>
            </div>
          </div>
          
          {/* Compact Search and Filters */}
          <div className="mb-3">
            <div className="flex gap-2">
              <div className="flex-1">
                <SearchInput
                  placeholder="Search teams..."
                  onSearch={handleSearch}
                  size="sm"
                />
              </div>
              <Button
                variant="secondary"
                size="sm"
                icon={<FunnelIcon />}
                className="relative flex-shrink-0"
              >
                {(filters.type || filters.status || filters.visibility) && (
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-orange-500 rounded-full"></div>
                )}
              </Button>
            </div>
          </div>

          {/* Selection Actions Bar */}
          {selectedTeams.length > 0 && (
            <div className="mb-3">
              <Card padding="sm" className="bg-orange-50 border-orange-200">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-orange-800">
                    {selectedTeams.length} selected
                  </span>
                  <div className="w-px h-4 bg-orange-300"></div>
                  <div className="flex gap-1">
                    <Button
                      variant="danger"
                      size="xs"
                      onClick={handleBulkDelete}
                      icon={<TrashIcon />}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto p-6">
          {error && (
            <Card className="mb-6 border-red-200 bg-red-50">
              <div className="flex items-center gap-3">
                <ExclamationTriangleIcon className="w-5 h-5 text-red-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-800">Error loading teams</p>
                  <p className="text-sm text-red-600">{error}</p>
                </div>
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => setError('')}
                  className="ml-auto text-red-600 hover:text-red-700"
                >
                  ×
                </Button>
              </div>
            </Card>
          )}

          {/* Teams Table */}
          <div className="overflow-x-auto">
            <Table
              columns={columns}
              data={teams}
              loading={loading}
              onRowClick={handleRowClick}
              onSort={handleSort}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              emptyState={{
                icon: <UserGroupIconSolid className="w-12 h-12 text-gray-400" />,
                title: teams.length === 0 ? "No teams found" : "No teams match your search",
                description: teams.length === 0 
                  ? "Create your first team to organize users and manage permissions"
                  : "Try adjusting your search or filter criteria",
                action: teams.length === 0 ? (
                  <Button
                    variant="primary"
                    onClick={() => setShowNewTeamModal(true)}
                    icon={<PlusIcon />}
                  >
                    Create Team
                  </Button>
                ) : null
              }}
            />
          </div>

          {/* Load More */}
          {hasMore && teams.length > 0 && (
            <div className="flex justify-center pt-6">
              <Button
                variant="ghost"
                onClick={() => fetchTeams(currentPage + 1, true)}
                loading={loadingMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading...' : 'Load More'}
              </Button>
            </div>
          )}

          {!hasMore && teams.length > 0 && (
            <div className="text-center pt-6">
              <span className="text-sm text-gray-500">No more teams to load</span>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <TeamModal
        open={showNewTeamModal}
        onClose={() => setShowNewTeamModal(false)}
        onSubmit={handleCreateTeam}
        type={teamType}
        loading={loading}
      />

      <EditTeamSidePanel
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedTeam(null);
        }}
        onSubmit={handleEditTeam}
        team={selectedTeam}
        loading={loading}
        showNotification={showNotification}
      />

      <MembersModal
        open={showMembersModal}
        onClose={() => {
          setShowMembersModal(false);
          setSelectedTeam(null);
        }}
        team={selectedTeam}
        onMembersUpdated={fetchTeams}
      />

      <TeamDetailsModal
        open={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedTeam(null);
        }}
        team={selectedTeam}
        showNotification={showNotification}
        onEdit={() => {
          setShowDetailsModal(false);
          setShowEditModal(true);
        }}
        onUpdateMembers={async (updates) => {
          try {
            const response = await axiosInstance.put(`/api/business/teams/${selectedTeam.id}`, updates);
            if (response.data.success) {
              await fetchTeams();
              return true;
            }
          } catch (error) {
            logger.error('Error updating team:', error);
            throw error;
          }
        }}
      />

      <EmailSettingsModal
        open={showEmailSettingsModal}
        onClose={() => {
          setShowEmailSettingsModal(false);
          setSelectedTeam(null);
        }}
        team={selectedTeam}
        onSettingsUpdated={fetchTeams}
      />

      {/* Filter Modal */}
      {showFilterMenu && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-lg p-6 max-w-md w-full mx-4 border border-gray-200 dark:border-gray-800">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Filter Teams</h3>
              <button
                onClick={() => setShowFilterMenu(false)}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Team Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Team Type
                </label>
                <select
                  value={filters.type}
                  onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">All Types</option>
                  <option value="team">Team</option>
                  <option value="department">Department</option>
                  <option value="project">Project</option>
                </select>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              {/* Visibility Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Visibility
                </label>
                <select
                  value={filters.visibility}
                  onChange={(e) => setFilters(prev => ({ ...prev, visibility: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">All Visibility</option>
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </select>
              </div>
            </div>

            <div className="flex justify-between mt-6">
              <Button
                variant="secondary"
                onClick={() => {
                  setFilters({ type: '', status: '', visibility: '' });
                  setCurrentPage(1);
                }}
                className="flex-shrink-0"
              >
                Clear Filters
              </Button>
              <div className="flex space-x-3">
                <Button
                  variant="secondary"
                  onClick={() => setShowFilterMenu(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    setCurrentPage(1);
                    setShowFilterMenu(false);
                    fetchTeams(1);
                  }}
                >
                  Apply Filters
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Teams;